export type KeyedResourceLoad<T> = {
  key: string;
  force?: boolean;
  cached: boolean;
  request: () => Promise<T>;
  onStart: () => void;
  onSuccess: (value: T) => void;
  onError: (message: string) => void;
  onFinish: () => void;
  errorMessage: string;
};

export function createKeyedResourceLoader() {
  const requests = new Map<string, Promise<void>>();
  const followUps = new Map<string, Promise<void>>();
  let generation = 0;
  const keyGenerations = new Map<string, number>();

  const load = <T,>({ key, force = false, cached, request, onStart, onSuccess, onError, onFinish, errorMessage }: KeyedResourceLoad<T>): Promise<void> => {
    if (!force && cached) return Promise.resolve();
    const active = requests.get(key);
    if (active) {
      if (!force) return active;
      const queued = followUps.get(key);
      if (queued) return queued;
      const queuedGeneration = generation;
      const queuedKeyGeneration = keyGenerations.get(key);
      const followUp = active.catch(() => undefined).then(() => {
        if (queuedGeneration !== generation || queuedKeyGeneration !== keyGenerations.get(key)) return;
        followUps.delete(key);
        return load({ key, force: true, cached: false, request, onStart, onSuccess, onError, onFinish, errorMessage });
      });
      followUps.set(key, followUp);
      return followUp;
    }
    onStart();
    const requestGeneration = generation;
    const keyGeneration = keyGenerations.get(key);
    const next = request()
      .then((value) => {
        if (requestGeneration === generation && keyGeneration === keyGenerations.get(key) && !followUps.has(key)) onSuccess(value);
      })
      .catch((error) => {
        if (requestGeneration === generation && keyGeneration === keyGenerations.get(key) && !followUps.has(key)) {
          onError(error instanceof Error ? error.message : errorMessage);
        }
        throw error;
      })
      .finally(() => {
        if (requests.get(key) === next) requests.delete(key);
        if (requestGeneration === generation && keyGeneration === keyGenerations.get(key) && !followUps.has(key)) onFinish();
      });
    requests.set(key, next);
    return next;
  };
  return {
    load,
    invalidate: (key: string) => {
      keyGenerations.set(key, (keyGenerations.get(key) || 0) + 1);
      requests.delete(key);
      followUps.delete(key);
    },
    clear: () => {
      generation += 1;
      keyGenerations.clear();
      requests.clear();
      followUps.clear();
    },
  };
}

export type ResourceLoad<T> = Omit<KeyedResourceLoad<T>, "key">;

export function createResourceLoader() {
  const loader = createKeyedResourceLoader();
  return {
    load: <T>(options: ResourceLoad<T>) => loader.load({ ...options, key: "resource" }),
    clear: loader.clear,
  };
}
